import React from 'react';
import { Link as RRLink } from 'react-router-dom';
import { Box } from 'pounceV2';
import { useTheme } from '@mui/material';
import useSidesheetV3 from 'Hooks/useSidesheetV3';

interface PantherDeepLinkV3Props {
  /** Same-origin path emitted by the AI (validated upstream as same-origin). */
  to: string;
  children: React.ReactNode;
}

/**
 * Renders an inline highlighted link to a Console route via React Router. Uses
 * `<RRLink>` so default-click navigates within the SPA (no full page reload),
 * while cmd+click / middle-click preserves the standard browser behavior of
 * opening in a new tab.
 *
 * On primary click, the AI sidesheet is dismissed synchronously before the
 * SPA navigates. The AI conversation is page-scoped — clicking a deep link is
 * "leave this page" and should land on a clean URL. Without this dismissal the
 * sidesheet's `conversationId`-to-URL sync (in
 * `AISummarySidePanel/.../AISummaryConversationContentV3`) races with the
 * pathname change: child effects fire before the parent provider's
 * auto-close, so the sync sees `isVisible=true` + a now-empty `urlConversationId`
 * and re-attaches the conversation param onto the destination URL.
 *
 * Modifier-key clicks (cmd / ctrl / shift / alt) and non-primary mouse buttons
 * skip the dismissal so the source tab keeps its state when the user opens the
 * link in a new tab via standard browser behavior.
 *
 * The markdown renderer in EnrichedAIResponseV3 swaps in this component for
 * any same-origin markdown anchor. The Go-side deeplinks registry bounds what
 * the AI emits; a hallucinated same-origin path falls through to React
 * Router's 404 on click.
 */
const PantherDeepLinkV3 = ({ to, children }: PantherDeepLinkV3Props) => {
  const {
    palette: { tokens },
  } = useTheme();
  const sidesheet = useSidesheetV3();

  const handleClick = React.useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
        return;
      }
      sidesheet?.hideSidesheet();
    },
    [sidesheet],
  );

  return (
    <Box
      as={RRLink}
      to={to}
      onClick={handleClick}
      display="inline"
      textDecoration="none"
      cursor="pointer"
      color={tokens.content.text.accent[14].base}
      fontWeight="medium"
      sx={{ '&:hover': { textDecoration: 'underline' } }}
    >
      {children}
    </Box>
  );
};

export default React.memo(PantherDeepLinkV3);
